/**
 * Pure phrasing helpers for the « Otto t'attend » queue (`get_missions_awaiting_human`).
 *
 * Kept out of the component because the queue is empty in prod (0 rows on
 * 15/07/2026 — the 42 existing escalations hang off missions canceled on 11/07),
 * so the rendering cannot be exercised in preview. Testing the wording here is the
 * only way to know it is right before the first real row shows up.
 */

/**
 * The reason Otto stopped, in plain French — the single phrasing used by both the
 * « Otto t'attend » queue and the shared mission card. `get_mission_brief` already
 * coalesces this to a fallback server-side, so a null here is a true edge (old data
 * or a bug). We answer with what Gilles can DO, not a developer note: an anomaly
 * message must address whoever can act on it, not whoever is stuck with it.
 */
export function waitingReasonText(reason: string | null | undefined): string {
  const trimmed = reason?.trim();
  return trimmed && trimmed.length > 0
    ? trimmed
    : "Otto s'est arrêté sans écrire ce qu'il attend. Ouvre la mission pour lui donner une consigne, ou arrête-la.";
}

/** True when the reason is missing — the surface paints that case as an anomaly. */
export function isWaitingReasonMissing(reason: string | null | undefined): boolean {
  return !reason || reason.trim().length === 0;
}

/**
 * How a stopped mission is classified server-side (`halt_kind`, exposed by
 * `get_missions_awaiting_human` / `get_mission_brief`):
 *   - `decision`  : a real human call — an autonomy-gate refusal, or a plan/result
 *                   Otto is holding for Gilles (`proposed`/`awaiting_validation`).
 *                   Belongs in « Il faut que tu tranches ».
 *   - `technical` : a token/wake-up cap, exhausted retries, a missing mailbox. Otto's
 *                   own snag, not Gilles's — belongs discreetly in « Otto travaille ».
 */
export type HaltKind = 'decision' | 'technical';

/** A technical halt is the one that gets the « Relancer » gesture, never zone 1. */
export function isTechnicalHalt(haltKind: string | null | undefined): boolean {
  return haltKind === 'technical';
}

/**
 * Statuses where Otto waits on a decision he framed himself — a plan to green-light
 * (`proposed`) or a finished result to review (`awaiting_validation`). These carry the
 * `Corriger`/`Arrêter` pair; every other decision just shows its waiting reason.
 * Kept here (not in a component) so no French status string is duplicated downstream.
 */
export function isCorrectableStatus(status: string | null | undefined): boolean {
  return status === 'proposed' || status === 'awaiting_validation';
}

/**
 * The single French line for a mission blocked on a human DECISION, keyed on the
 * status. `proposed`/`awaiting_validation` get a purpose-written line; anything else
 * falls back to the reason Otto wrote (or the actionable placeholder if he wrote none).
 */
export function awaitingDecisionText(
  status: string | null | undefined,
  reason: string | null | undefined,
): string {
  if (status === 'proposed') {
    return "Otto a préparé un plan et attend ton feu vert avant de l'exécuter.";
  }
  if (status === 'awaiting_validation') {
    return "Otto a terminé et attend ta relecture avant d'aller plus loin.";
  }
  return waitingReasonText(reason);
}

/**
 * The French line for a TECHNICAL halt. It deliberately says nothing of the
 * machinery that stopped Otto — no token budget, no cap, no retry count (règle du
 * cockpit : jamais de « budget de tokens » en façade). Gilles reads « Otto a calé »,
 * and the one thing he can do about it: relaunch.
 */
export function technicalHaltText(): string {
  return "Otto a calé sur un obstacle technique. Relance-le pour qu'il reprenne où il s'est arrêté.";
}

/*
 * `plannedCountText` lived here and is gone (15/07/2026).
 *
 * It rendered "Otto n'enchaînera rien tout seul" whenever `planned_count` was 0 —
 * which, on THIS queue, is always: escalating freezes every pending action to
 * `awaiting_human`, so the count is 0 by construction. The row therefore promised
 * inaction unconditionally, and the promise is now false twice over: correcting the
 * mission does resume it, and Otto is free to act alone on everything except
 * sending a client email (the one rule left — cf. _shared/autonomy.ts). A sentence
 * that is always printed and never checked is not information, it is reassurance.
 */

/**
 * The composition line under « Il faut que tu tranches » — « 6 brouillons à envoyer ·
 * 1 RDV à valider · 2 missions en attente ». The zone's total mixes things the
 * messagerie strip does not (it counts missions only), and reading « 8 » here after
 * « 2 » there read as two different queues (04/09/2026). A zero part is left unwritten.
 * Appointment proposals (14/09/2026) are their own part: a mission paused on a proposal
 * is shown as that proposal, not counted twice.
 */
export function decisionBreakdownText(draftCount: number, missionCount: number, proposalCount = 0): string {
  const parts: string[] = [];
  if (draftCount > 0) {
    parts.push(draftCount === 1 ? '1 brouillon à envoyer' : `${draftCount} brouillons à envoyer`);
  }
  if (proposalCount > 0) {
    parts.push(proposalCount === 1 ? '1 RDV à valider' : `${proposalCount} RDV à valider`);
  }
  if (missionCount > 0) {
    parts.push(missionCount === 1 ? '1 mission en attente' : `${missionCount} missions en attente`);
  }
  return parts.join(' · ');
}


/** Une sorte de touchpoint et son nombre, tels que `mission_question_freshness` les rend. */
export interface FreshKind {
  kind: string;
  count: number;
}

/**
 * Les sortes d'événements, au singulier et au pluriel, dans l'ordre où elles méritent
 * d'être lues. L'ordre n'est pas décoratif : un rendez-vous refusé ou une facture partie
 * tranchent une question à eux seuls, un mail de plus demande d'aller voir. La revue du
 * 18/09/2026 a montré que la carte affichait « 3 mails depuis » là où le fait décisif
 * était « Hervé Pacquetet a refusé l'invitation ».
 *
 * Ce qui n'y figure pas est volontaire : `mail_out`, `note`, `sms_out` et les automates
 * sont écartés côté SQL (`actor_kind`), parce que ce que nous écrivons ne périme pas notre
 * propre question. Un appel que nous passons, si (22/10/2026) : c'est un geste humain, et
 * « j'ai déjà appelé » rend la question caduque ; il arrive ici sous `call`.
 */
const KIND_LABELS: Readonly<Record<string, readonly [string, string]>> = {
  appointment_declined: ['rendez-vous refusé', 'rendez-vous refusés'],
  appointment_cancelled: ['rendez-vous annulé', 'rendez-vous annulés'],
  invoice: ['facture émise', 'factures émises'],
  draft_change: ['brouillon archivé', 'brouillons archivés'],
  appointment_created: ['rendez-vous prévu', 'rendez-vous prévus'],
  call: ['appel', 'appels'],
  meeting_report: ['compte-rendu', 'comptes-rendus'],
  widget_message: ["message dans l'app", "messages dans l'app"],
  sms_in: ['SMS reçu', 'SMS reçus'],
  mail_in: ['mail', 'mails'],
};

const KIND_ORDER: readonly string[] = Object.keys(KIND_LABELS);

/** Parts gardées dans la ligne : au-delà, on dit « et N autres ». */
const MAX_PARTS = 3;

/**
 * Ce qui est arrivé DEPUIS la question, en une ligne — le complément qui manquait à
 * chaque carte de « Il faut que tu tranches ».
 *
 * Six des dix-huit questions en attente du 18/09/2026 posaient une prémisse devenue
 * fausse, et elles étaient présentées exactement comme les douze qui tenaient. Cette
 * ligne fait la différence à l'œil nu, sans qu'aucun modèle ait relu quoi que ce soit.
 *
 * On énonce le FAIT, jamais un verdict : la base sait qu'un rendez-vous a été refusé,
 * elle ne sait pas si ça change la réponse. Gilles conclut.
 */
export function freshSinceQuestionText(
  kinds: readonly FreshKind[] | null | undefined,
  lastFrom: string | null | undefined,
  lastAt: string | null | undefined,
  timeZone: string,
): string | null {
  const present = (kinds ?? [])
    .filter((k) => k && typeof k.count === 'number' && k.count > 0 && KIND_LABELS[k.kind])
    .sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind));
  if (present.length === 0) return null;

  const shown = present.slice(0, MAX_PARTS).map((k) => {
    const [singulier, pluriel] = KIND_LABELS[k.kind];
    return `${k.count} ${k.count === 1 ? singulier : pluriel}`;
  });
  const reste = present.length - shown.length;
  if (reste > 0) shown.push(reste === 1 ? '1 autre' : `${reste} autres`);

  const who = lastFrom?.trim();
  const when = formatShortDateTime(lastAt, timeZone);
  const queue = [who, when].filter(Boolean).join(', ');
  return queue ? `${shown.join(' · ')} — dernier : ${queue}` : shown.join(' · ');
}

/** « 18/09 09h36 » dans le fuseau de l'entreprise, ou null si la date est absente ou illisible. */
function formatShortDateTime(iso: string | null | undefined, timeZone: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', timeZone });
  const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone });
  return `${date} ${time.replace(':', 'h')}`;
}
